import { useQuery, useQueryClient } from '@tanstack/react-query';
import { quickbooksService } from '@/services/api/quickbooks.service';
import type { QuickbooksState } from '@/types/quickbooks';

export const quickbooksKey = ['quickbooks', 'status'] as const;

interface StatusOptions {
  staleTime?: number;
  refetchOnWindowFocus?: boolean;
  retry?: number | false;
}

/** The connection status. `enabled` is the quickbooks flag: nothing is requested while it is off or still loading. */
export function useQuickbooksStatus(enabled = true, options: StatusOptions = {}) {
  return useQuery({ queryKey: quickbooksKey, queryFn: quickbooksService.status, enabled, staleTime: options.staleTime ?? 30_000, retry: options.retry ?? 1, refetchOnWindowFocus: options.refetchOnWindowFocus });
}

/** The company's accounts and tax codes, read from QuickBooks when the Choose accounts card opens. Never cached for long: they are the company's, not ours. */
export function useQuickbooksAccounts(enabled: boolean) {
  return useQuery({ queryKey: ['quickbooks', 'accounts'], queryFn: quickbooksService.accounts, enabled, staleTime: 60_000, retry: false });
}
export function useQuickbooksTaxCodes(enabled: boolean) {
  return useQuery({ queryKey: ['quickbooks', 'tax-codes'], queryFn: quickbooksService.taxCodes, enabled, staleTime: 60_000, retry: false });
}

/** Every mutating call answers the new status: put it in the cache instead of refetching. */
export function useSetQuickbooksStatus() {
  const client = useQueryClient();
  return (state: QuickbooksState) => client.setQueryData(quickbooksKey, state);
}

/** A business with a QuickBooks connection that is still on (connected or needing a reconnect). Not connected, never connected and a status that could not be read (404 while the flag is off) are all false. */
export function isLiveConnection(state: QuickbooksState | undefined): boolean {
  const c = state?.connection;
  return !!c && c.status !== 'disconnected';
}
